import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import BrandLogo from "@/components/common/BrandLogo";
import { getServicesByCategory, getServiceRoute } from "@/data/services";

export default function Header() {
  const { t, to, lang, swapLangPath } = useLang();
  const { openForm, openCalendly } = useConversion();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'assurances' | 'cabinet' | null
  const navRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setOpenMenu(null); setMobileOpen(false); }
    };
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  const otherLangPath = swapLangPath(location.pathname);
  const homeAria = lang === "fr" ? "NEKASSUR — Retour à la page d'accueil" : "NEKASSUR — Back to homepage";
  const goHome = () => {
    setMobileOpen(false);
    setOpenMenu(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const complexNeed = t.form.needs[12];
  const L =
    lang === "fr"
      ? {
          assurances: "Nos assurances", complex: "Situations complexes", frontaliers: "Frontaliers", cabinet: "Le cabinet", conseils: "Conseils",
          allParticuliers: "Toutes les solutions pour les particuliers", allPros: "Toutes les solutions pour les professionnels",
          about: "À propos", faq: "FAQ", contact: "Contact",
          colParticuliers: "Particuliers", colPros: "Professionnels & entreprises", colEpargne: "Épargne & retraite",
        }
      : {
          assurances: "Our insurance", complex: "Complex situations", frontaliers: "Cross-border", cabinet: "The firm", conseils: "Insights",
          allParticuliers: "All solutions for individuals", allPros: "All solutions for professionals",
          about: "About", faq: "FAQ", contact: "Contact",
          colParticuliers: "Individuals", colPros: "Professionals & businesses", colEpargne: "Savings & retirement",
        };

  const cols = [
    { title: L.colParticuliers, items: getServicesByCategory("particuliers") },
    { title: L.colPros, items: getServicesByCategory("professionnels") },
    { title: L.colEpargne, items: getServicesByCategory("epargne") },
  ];

  const linkBase = "whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-nek-ink transition-colors hover:text-nek-blue";

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:m-3 focus:rounded-lg focus:bg-nek-navy focus:px-4 focus:py-2 focus:text-white">
        {t.header.skip}
      </a>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
          scrolled ? "border-nek-border bg-nek-light/95 backdrop-blur-md py-2 shadow-soft" : "border-transparent bg-nek-light/85 backdrop-blur-md py-2.5"
        }`}
      >
        <div className="nek-container flex items-center justify-between gap-4">
          <Link to={to("")} data-testid="logo-link" aria-label={homeAria} onClick={goHome} className="inline-flex shrink-0 items-center rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nek-teal focus-visible:ring-offset-2">
            <BrandLogo className="h-9 w-auto lg:h-10" />
          </Link>

          {/* Desktop nav */}
          <nav ref={navRef} aria-label="Principale" className="hidden min-[1180px]:flex items-center gap-1">
            {/* Nos assurances mega menu */}
            <div className="relative" onMouseEnter={() => setOpenMenu("assurances")} onMouseLeave={() => setOpenMenu(null)}>
              <button
                type="button"
                data-testid="nav-assurances"
                aria-expanded={openMenu === "assurances"}
                onClick={() => setOpenMenu("assurances")}
                onFocus={() => setOpenMenu("assurances")}
                className={`inline-flex items-center gap-1 ${linkBase} ${openMenu === "assurances" ? "text-nek-blue" : ""}`}
              >
                {L.assurances}
                <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "assurances" ? "rotate-180" : ""}`} />
              </button>
              {openMenu === "assurances" && (
                <div data-testid="mega-assurances" className="absolute left-0 top-full pt-3">
                  <div className="w-[720px] rounded-2xl border border-nek-border bg-white p-6 shadow-lift">
                    <div className="grid grid-cols-3 gap-6">
                      {cols.map((col) => (
                        <div key={col.title}>
                          <p className="text-xs font-semibold uppercase tracking-wider text-nek-teal">{col.title}</p>
                          <ul className="mt-3 space-y-1">
                            {col.items.map((s) => (
                              <li key={s.slug}>
                                <Link to={to(getServiceRoute(s))} className="block rounded-lg px-2 py-1.5 text-sm text-nek-ink transition-colors hover:bg-nek-light hover:text-nek-blue">
                                  {s[lang].name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 flex flex-wrap gap-4 border-t border-nek-border pt-4 text-sm font-semibold">
                      <Link to={to(t.nav.particuliers.path)} className="text-nek-blue hover:underline">{L.allParticuliers}</Link>
                      <Link to={to(t.nav.professionnels.path)} className="text-nek-blue hover:underline">{L.allPros}</Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button type="button" data-testid="nav-complex" onClick={() => openForm({ need: complexNeed, source: "nav-complex" })} className={linkBase}>
              {L.complex}
            </button>

            <NavLink to={to(t.nav.frontaliers.path)} data-testid="nav-frontaliers" className={({ isActive }) => `${linkBase} ${isActive ? "text-nek-blue" : ""}`}>
              {L.frontaliers}
            </NavLink>

            {/* Le cabinet dropdown */}
            <div className="relative" onMouseEnter={() => setOpenMenu("cabinet")} onMouseLeave={() => setOpenMenu(null)}>
              <button
                type="button"
                data-testid="nav-cabinet"
                aria-expanded={openMenu === "cabinet"}
                onClick={() => setOpenMenu("cabinet")}
                onFocus={() => setOpenMenu("cabinet")}
                className={`inline-flex items-center gap-1 ${linkBase} ${openMenu === "cabinet" ? "text-nek-blue" : ""}`}
              >
                {L.cabinet}
                <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "cabinet" ? "rotate-180" : ""}`} />
              </button>
              {openMenu === "cabinet" && (
                <div data-testid="dropdown-cabinet" className="absolute left-0 top-full pt-3">
                  <div className="w-56 rounded-2xl border border-nek-border bg-white p-2 shadow-lift">
                    <Link to={to(t.nav.about.path)} className="block rounded-lg px-3 py-2 text-sm text-nek-ink hover:bg-nek-light hover:text-nek-blue">{L.about}</Link>
                    <Link to={{ pathname: to(""), hash: "#faq" }} className="block rounded-lg px-3 py-2 text-sm text-nek-ink hover:bg-nek-light hover:text-nek-blue">{L.faq}</Link>
                    <Link to={to("/contact")} className="block rounded-lg px-3 py-2 text-sm text-nek-ink hover:bg-nek-light hover:text-nek-blue">{L.contact}</Link>
                  </div>
                </div>
              )}
            </div>

            <NavLink to={to("/conseils")} data-testid="nav-conseils" className={({ isActive }) => `${linkBase} ${isActive ? "text-nek-blue" : ""}`}>
              {L.conseils}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 sm:gap-2.5">
            <Link
              to={otherLangPath}
              data-testid="lang-switch"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-nek-border px-2.5 py-2 text-sm font-semibold text-nek-navy transition-colors hover:border-nek-blue hover:text-nek-blue"
              aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}
            >
              <Globe className="h-4 w-4" strokeWidth={2.2} />
              {lang === "fr" ? "EN" : "FR"}
            </Link>
            <button type="button" data-testid="header-appointment-btn" onClick={openCalendly} className="hidden md:inline-flex btn-coral !py-2.5 !px-4 text-sm whitespace-nowrap">
              {t.cta.bookAppointment}
            </button>
            <button
              type="button"
              className="min-[1180px]:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-nek-border text-nek-navy"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              aria-label={t.header.menu}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="min-[1180px]:hidden max-h-[80vh] overflow-y-auto border-t border-nek-border bg-white" data-testid="mobile-menu">
            <nav aria-label="Mobile" className="nek-container flex flex-col py-4">
              <Link to={to("")} onClick={goHome} aria-label={homeAria} data-testid="mobile-logo-link" className="mb-3 inline-flex w-fit rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nek-teal">
                <BrandLogo className="h-9 w-auto" />
              </Link>

              <p className="mt-2 px-3 text-xs font-semibold uppercase tracking-wider text-nek-teal">{L.assurances}</p>
              {cols.map((col) => (
                <div key={col.title} className="mt-1">
                  <p className="px-3 pt-2 text-xs font-medium text-nek-gray">{col.title}</p>
                  {col.items.map((s) => (
                    <Link key={s.slug} to={to(getServiceRoute(s))} className="block rounded-lg px-4 py-2.5 text-sm text-nek-ink hover:bg-nek-light">
                      {s[lang].name}
                    </Link>
                  ))}
                </div>
              ))}

              <div className="mt-3 border-t border-nek-border pt-3">
                <button type="button" onClick={() => { setMobileOpen(false); openForm({ need: complexNeed, source: "nav-complex" }); }} className="block w-full rounded-lg px-4 py-3 text-left text-base font-medium text-nek-ink hover:bg-nek-light">
                  {L.complex}
                </button>
                <Link to={to(t.nav.frontaliers.path)} className="block rounded-lg px-4 py-3 text-base font-medium text-nek-ink hover:bg-nek-light">{L.frontaliers}</Link>
                <Link to={to(t.nav.about.path)} className="block rounded-lg px-4 py-3 text-base font-medium text-nek-ink hover:bg-nek-light">{L.about}</Link>
                <Link to={{ pathname: to(""), hash: "#faq" }} className="block rounded-lg px-4 py-3 text-base font-medium text-nek-ink hover:bg-nek-light">{L.faq}</Link>
                <Link to={to("/contact")} className="block rounded-lg px-4 py-3 text-base font-medium text-nek-ink hover:bg-nek-light">{L.contact}</Link>
                <Link to={to("/conseils")} className="block rounded-lg px-4 py-3 text-base font-medium text-nek-ink hover:bg-nek-light">{L.conseils}</Link>
              </div>

              <div className="mt-3 flex flex-col gap-2 px-3">
                <button type="button" onClick={openCalendly} className="btn-coral w-full">{t.cta.bookAppointment}</button>
                <Link to={otherLangPath} className="btn-outline w-full">
                  <Globe className="h-4 w-4" /> {lang === "fr" ? "English" : "Français"}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
